import React from "react";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { DEFICIENCY_DOCUMENT_PREFIXES } from "@services/deficiencies/types";
import { uploadAuthenticatedDocumentToAzure } from "@services/uploadApi";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

type UploadDeficiencyDocumentsParams = {
  files: File[];
  startIdx: number;
  sessionId: number;
};

type UseDeficiencyDocumentUploadOptions = {
  storeKey: string;
  certificateType: string;
  fallbackFileNamePrefix: string;
  errorNotification: {
    titleKey: string;
    descriptionKey: string;
  };
};

export const getDeficiencyErrorMessage = (error: unknown): string | undefined => {
  const parsedError = error as {
    response?: { data?: { detail?: unknown; message?: unknown; title?: unknown } };
    data?: { detail?: unknown; message?: unknown; title?: unknown };
    detail?: unknown;
    message?: unknown;
  };

  const errorMessage =
    parsedError.response?.data?.detail ??
    parsedError.response?.data?.message ??
    parsedError.response?.data?.title ??
    parsedError.data?.detail ??
    parsedError.data?.message ??
    parsedError.data?.title ??
    parsedError.detail ??
    parsedError.message;

  return typeof errorMessage === "string" && errorMessage.trim().length > 0
    ? errorMessage
    : undefined;
};

export const isDeficiencyUploadedEntry = (
  entry: UploadEntry,
): entry is Partial<File> & { name: string; url: string; [key: string]: unknown } => {
  return !(entry instanceof File) && typeof entry.url === "string" && entry.url.trim().length > 0;
};

export const getUploadedDeficiencyDocumentUrls = (entries?: UploadEntry[]) => {
  return (entries ?? []).filter(isDeficiencyUploadedEntry).map((entry) => entry.url);
};

const replaceEntriesAtIndex = (
  entries: UploadEntry[],
  startIdx: number,
  replacements: UploadEntry[],
) => {
  const next = [...entries];

  replacements.forEach((entry, offset) => {
    next[startIdx + offset] = entry;
  });

  return next;
};

const removeEntriesAtIndexRange = (entries: UploadEntry[], startIdx: number, count: number) => {
  return entries.filter((_, index) => index < startIdx || index >= startIdx + count);
};

const buildUploadedEntries = ({
  files,
  documentUrls,
  startIdx,
  fallbackFileNamePrefix,
}: {
  files: File[];
  documentUrls: string[];
  startIdx: number;
  fallbackFileNamePrefix: string;
}): UploadEntry[] => {
  return documentUrls.map((url, index) => ({
    name: files[index]?.name ?? `${fallbackFileNamePrefix}-${startIdx + index + 1}`,
    url,
    type: files[index]?.type ?? "",
  }));
};

export const useDeficiencyDocumentUpload = ({
  storeKey,
  certificateType,
  fallbackFileNamePrefix,
  errorNotification,
}: UseDeficiencyDocumentUploadOptions) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const persistedEntries = useUploadStore(
    (state) => state.filesByKey[storeKey] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const [uploadEntries, setUploadEntries] = React.useState<UploadEntry[]>(persistedEntries);
  const sessionRef = React.useRef(0);

  React.useEffect(() => {
    setUploadEntries(persistedEntries);
  }, [persistedEntries]);

  const syncUploadEntries = React.useCallback(
    (updater: (prev: UploadEntry[]) => UploadEntry[]) => {
      setUploadEntries(updater);
      updateFilesForKey(storeKey, updater);
    },
    [storeKey, updateFilesForKey],
  );

  const uploadMutation = useMutation({
    mutationFn: async ({ files, startIdx, sessionId }: UploadDeficiencyDocumentsParams) => {
      const documentUrls: string[] = [];

      for (const file of files) {
        const uploadResult = await uploadAuthenticatedDocumentToAzure({
          file,
          prefixes: DEFICIENCY_DOCUMENT_PREFIXES,
          certificateType,
        });

        documentUrls.push(uploadResult.documentUrl);
      }

      return { documentUrls, files, startIdx, sessionId };
    },
    onSuccess: ({ documentUrls, files, startIdx, sessionId }) => {
      if (sessionId !== sessionRef.current) {
        return;
      }

      const uploadedEntries = buildUploadedEntries({
        files,
        documentUrls,
        startIdx,
        fallbackFileNamePrefix,
      });

      syncUploadEntries((prev) => replaceEntriesAtIndex(prev, startIdx, uploadedEntries));
    },
    onError: (error, variables) => {
      if (variables.sessionId !== sessionRef.current) {
        return;
      }

      syncUploadEntries((prev) =>
        removeEntriesAtIndexRange(prev, variables.startIdx, variables.files.length),
      );

      notify({
        title: t(errorNotification.titleKey),
        description: getDeficiencyErrorMessage(error) ?? t(errorNotification.descriptionKey),
        type: "error",
      });
    },
  });

  const handleUpload = React.useCallback(
    (files: File[]) => {
      if (!files.length) {
        return;
      }

      uploadMutation.mutate({
        files,
        startIdx: uploadEntries.length,
        sessionId: sessionRef.current,
      });
    },
    [uploadEntries.length, uploadMutation],
  );

  const resetUpload = React.useCallback(() => {
    sessionRef.current += 1;
    setUploadEntries([]);
    clearFilesForKey(storeKey);
  }, [clearFilesForKey, storeKey]);

  const uploadedDocumentUrls = React.useMemo(
    () => getUploadedDeficiencyDocumentUrls(uploadEntries),
    [uploadEntries],
  );

  return {
    uploadEntries,
    setUploadEntries,
    handleUpload,
    resetUpload,
    uploadedDocumentUrls,
    isUploading: uploadMutation.isPending,
  };
};
